"use client"

import type { ReactNode } from "react"
import {
  Bot,
  BotOff,
  Briefcase,
  Loader2,
  MessageSquare,
  MoreVertical,
  Plus,
  Shield,
  ShieldOff,
  Sparkles,
  UserCheck,
  UserX,
  Zap,
  type LucideIcon,
} from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ChannelIcon } from "@/components/ui/channel-icon"
import { InboxBackButton } from "@/components/ui/conversation/inbox-layout"
import { SessionOutcomeBadge } from "@/components/ui/conversation/session-outcome-badge"
import { BANNER_TONE_STYLES, STATE_TONE_STYLES } from "@/components/ui/conversation/thread-tones"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { contactInitials } from "@/lib/conversation/initials"
import type {
  ThreadAction,
  ThreadBanner,
  ThreadFact,
  ThreadActionIcon,
  ThreadActionId,
  ThreadActionState,
  ThreadQuality,
  ThreadStateBadge,
} from "@/lib/conversation/thread-header"
import { cn } from "@/lib/utils"

/**
 * A surface control follows the header's two placements: an icon button on the
 * row, a labelled full-width entry in the overflow menu.
 */
export const HEADER_CONTROL_STYLES = {
  button: "w-full justify-start gap-2 xl:w-10 xl:justify-center",
  label: "xl:sr-only",
} as const

interface ConversationHeaderProps {
  avatarUrl: string | null
  title: string
  /**
   * Channel type key for the badge on the avatar — a messaging channel, `email`
   * or `phone`. Null where the surface does not know it: no badge is better than
   * the unknown-channel glyph, which reads as a broken channel rather than a
   * missing one.
   */
  channelKind: string | null
  /** The line under the title. Surface-owned: handles, accounts and deep links differ per inbox. */
  identity?: ReactNode
  /** Above the banner, because the banner is about the conversation and these are about it too. */
  facts?: ThreadFact[]
  state: ThreadStateBadge[]
  actions: ThreadAction[]
  banners?: ThreadBanner[]
  /** The verdict on the last judged session, beside the state badges. */
  quality?: ThreadQuality
  /** Offered only where the surface lets the operator correct the verdict. */
  onCorrectQuality?: () => void
  /** The action currently running. Its button spins and the whole bar is locked. */
  pendingActionId?: ThreadActionId | null
  onAction?: (action: ThreadAction) => void
  /** Surface-specific controls after the action bar: refresh, «open in agent inbox». */
  children?: ReactNode
  /** Below `split` the thread replaced the list; this is the way back. */
  onBack?: () => void
}

/**
 * The strip above a conversation, in both inboxes: who, where, what state, and
 * what can be done about it.
 *
 * It renders descriptors and reports presses. Confirming, mutating and deciding
 * what is pending all belong to the surface — the agent inbox asks with its own
 * dialog and the unified inbox does not, and neither belongs in here.
 */
export function ConversationHeader({
  avatarUrl,
  title,
  channelKind,
  identity,
  facts = [],
  state,
  actions,
  banners = [],
  quality,
  onCorrectQuality,
  pendingActionId,
  onAction,
  children,
  onBack,
}: ConversationHeaderProps) {
  const pills = facts.filter((entry) => !entry.block)
  const blocks = facts.filter((entry) => entry.block)
  const prominent = actions.filter((action) => action.prominent)
  const secondary = actions.filter((action) => !action.prominent)

  return (
    <>
      <div className="flex flex-shrink-0 items-center gap-2 border-b bg-background px-3 py-2 split:gap-3 split:px-4 split:py-3">
        {onBack && <InboxBackButton onBack={onBack} />}
        <div className="relative flex-shrink-0">
          <Avatar className="h-8 w-8 split:h-9 split:w-9">
            <AvatarImage src={avatarUrl ?? undefined} />
            <AvatarFallback className="text-xs">{contactInitials(title)}</AvatarFallback>
          </Avatar>
          {channelKind && (
            <div className="absolute -bottom-0.5 -right-0.5">
              <ChannelIcon channelType={channelKind} size={12} />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-sm font-medium">{title}</h2>
            {state.map((badge) => (
              <Badge
                key={badge.label}
                variant="outline"
                className={cn("whitespace-nowrap px-1.5 py-0 text-[10px]", STATE_TONE_STYLES[badge.tone])}
              >
                {badge.label}
              </Badge>
            ))}
            {quality && <SessionOutcomeBadge quality={quality} onCorrect={onCorrectQuality} />}
          </div>
          {identity}
        </div>

        <ThreadActionBar actions={prominent} pendingActionId={pendingActionId} onAction={onAction} />
        {/* From `xl` up every control is on the row: beside the list the pane is
            ~575 px there, the least that seats the name, the prominent action and
            four icons. Below it the row keeps the prominent action alone; the rest
            and the surface's own controls sit behind one button. */}
        {(secondary.length > 0 || !!children) && (
          <>
            <div className="hidden items-center gap-3 xl:flex">
              <ThreadActionBar actions={secondary} pendingActionId={pendingActionId} onAction={onAction} />
              {children}
            </div>
            <HeaderOverflowMenu actions={secondary} pendingActionId={pendingActionId} onAction={onAction}>
              {children}
            </HeaderOverflowMenu>
          </>
        )}
      </div>

      {facts.length > 0 && (
        // Pills, not `label: value` prose: the strip is scanned for one fact at a
        // time. A fact the source did not resolve is absent rather than empty.
        // Below `split` the pills scroll sideways in one row; a paragraph fact
        // keeps its own full-width row at every width, because a screen-wide block
        // inside a scrolling row is readable only by scrolling it.
        <dl className="flex-shrink-0 border-b bg-muted/40 px-4 py-2">
          {pills.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto split:flex-wrap split:overflow-visible">
              {pills.map((entry) => (
                <FactEntry key={entry.label} entry={entry} className="shrink-0 items-baseline rounded-full py-0.5 split:min-w-0 split:shrink" />
              ))}
            </div>
          )}
          {blocks.map((entry) => (
            // A paragraph is not a pill: a full round on three lines leaves the
            // first one touching the arc, and a baseline row drops the label to
            // the last line.
            <FactEntry key={entry.label} entry={entry} className={cn("w-full items-start rounded-md py-1", pills.length > 0 && "mt-1.5")} />
          ))}
        </dl>
      )}

      {banners.map((banner) => (
        <div
          key={banner.text}
          className={cn(
            "flex flex-shrink-0 items-center gap-2 border-b px-4 py-2 text-xs",
            BANNER_TONE_STYLES[banner.tone],
          )}
        >
          {banner.text}
        </div>
      ))}
    </>
  )
}

function FactEntry({ entry, className }: { entry: ThreadFact; className: string }) {
  return (
    <div className={cn("inline-flex gap-1 border bg-background px-2 text-[11px]", className)}>
      {entry.icon && (
        <span aria-hidden className="self-center">
          <ChannelIcon channelType={entry.icon} size={12} />
        </span>
      )}
      <dt className="flex-shrink-0 text-muted-foreground">{entry.label}</dt>
      {/* The separator earns its place: without it `Escalated yes` reads as a
          broken sentence, and the label cannot simply go — an address with no
          «Sender» in front of it is nobody's address. */}
      <span aria-hidden className="text-muted-foreground/40">·</span>
      <dd className={cn("font-medium", entry.block ? "whitespace-pre-wrap" : "truncate")}>
        {entry.value}
      </dd>
    </div>
  )
}

/**
 * A key, not a component: the descriptor is built in a query file and crosses a
 * server-action boundary. `Record` over the union is what makes a new key a
 * compile error here rather than a blank button in production.
 */
const ACTION_ICONS: Record<ThreadActionIcon, LucideIcon> = {
  "ai-active": Bot,
  "ai-paused": BotOff,
  shield: Shield,
  "shield-off": ShieldOff,
  exclude: UserX,
  include: UserCheck,
  "new-session": Plus,
  classify: Zap,
  draft: Sparkles,
  crm: Briefcase,
  feedback: MessageSquare,
}

/**
 * One group of the header's action buttons, as a fragment so the row's own gap
 * applies. One action at a time — the bar locks while any of them is running,
 * because two writes against the same conversation would race for the state each
 * reports.
 */
function ThreadActionBar({
  actions,
  pendingActionId,
  onAction,
}: {
  actions: ThreadAction[]
  pendingActionId?: ThreadActionId | null
  onAction?: (action: ThreadAction) => void
}) {
  return (
    <>
      {actions.map((action) => (
        <ActionButton
          key={action.id}
          action={action}
          isPending={pendingActionId === action.id}
          isLocked={pendingActionId != null}
          onAction={onAction}
        />
      ))}
    </>
  )
}

/**
 * The secondary actions and the surface's own controls, behind one button below
 * `xl`. `modal={false}`: a modal menu leaves `pointer-events: none` on the body
 * when an item opens a confirm dialog — the history is on the user-management page.
 */
function HeaderOverflowMenu({
  actions,
  pendingActionId,
  onAction,
  children,
}: {
  actions: ThreadAction[]
  pendingActionId?: ThreadActionId | null
  onAction?: (action: ThreadAction) => void
  children?: ReactNode
}) {
  const isLocked = pendingActionId != null

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 flex-shrink-0 xl:hidden"
          aria-label="More actions"
          title="More actions"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {actions.map((action) => {
          const Icon = ACTION_ICONS[action.icon]
          return (
            <DropdownMenuItem
              key={action.id}
              disabled={!isPressable(action, isLocked, onAction)}
              onSelect={() => onAction?.(action)}
              title={action.hint ?? action.label}
              variant={action.tone === "danger" && action.state === "active" ? "destructive" : "default"}
            >
              {pendingActionId === action.id ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Icon className="mr-2 h-4 w-4" />
              )}
              {action.label}
            </DropdownMenuItem>
          )
        })}
        {children && (
          <>
            {actions.length > 0 && <DropdownMenuSeparator />}
            <div className="flex items-center gap-1 px-1 py-1">{children}</div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ActionButton({
  action,
  isPending,
  isLocked,
  onAction,
}: {
  action: ThreadAction
  isPending: boolean
  isLocked: boolean
  onAction?: (action: ThreadAction) => void
}) {
  const Icon = ACTION_ICONS[action.icon]

  return (
    <Button
      variant={action.prominent ? PROMINENT_VARIANTS[action.state] : "ghost"}
      size={action.prominent ? "sm" : "icon"}
      onClick={() => onAction?.(action)}
      // `done` is history and `blocked` is a prerequisite; both stay visible so
      // the operator can read what happened or what is missing, and neither is
      // pressable.
      disabled={!isPressable(action, isLocked, onAction)}
      title={action.hint ?? action.label}
      className={actionStyle(action)}
    >
      {isPending ? (
        <Loader2 className={cn(action.prominent ? "mr-1 h-3.5 w-3.5" : "h-4 w-4", "animate-spin")} />
      ) : (
        <Icon className={action.prominent ? "mr-1 h-3.5 w-3.5" : "h-4 w-4"} />
      )}
      {action.prominent && action.label}
    </Button>
  )
}

const PRESSABLE: Record<ThreadActionState, boolean> = {
  available: true,
  active: true,
  done: false,
  blocked: false,
}

/** One action at a time, and only one the descriptor and the surface both allow. */
function isPressable(
  action: ThreadAction,
  isLocked: boolean,
  onAction?: (action: ThreadAction) => void,
): boolean {
  return !isLocked && PRESSABLE[action.state] && !!onAction
}

const PROMINENT_VARIANTS: Record<ThreadActionState, "default" | "outline"> = {
  available: "default",
  active: "outline",
  done: "outline",
  blocked: "outline",
}

/** Green while the AI is answering, amber once a person has taken over. */
const PROMINENT_STYLES: Record<ThreadActionState, string> = {
  available: "bg-green-600 text-white hover:bg-green-700",
  active: "border-amber-300 text-amber-600 hover:bg-amber-50",
  done: "text-muted-foreground",
  blocked: "text-muted-foreground",
}

/**
 * A blocking state outranks the shape it is drawn in: red first, then the prominent
 * button's own palette, then the plain icon's.
 */
function actionStyle(action: ThreadAction): string {
  if (action.tone === "danger" && action.state === "active") {
    return action.prominent ? DANGER_STYLES.prominent : DANGER_STYLES.icon
  }
  return action.prominent ? PROMINENT_STYLES[action.state] : ICON_STYLES[action.state]
}

/** A block someone should notice, not another shade of «this is on». */
const DANGER_STYLES = {
  icon: "text-red-600 bg-red-50 hover:bg-red-100 dark:text-red-400 dark:bg-red-950/40",
  prominent: "border-red-300 text-red-600 hover:bg-red-50 dark:border-red-900 dark:text-red-400",
}

const ICON_STYLES: Record<ThreadActionState, string> = {
  available: "",
  active: "text-violet-600 dark:text-violet-400",
  done: "text-muted-foreground",
  blocked: "text-muted-foreground",
}
